// Considered: DeployTargetPickerMolecule — rejected because the molecule duplicates dialog
// machinery with a bespoke overlay, dual backend, and grouped layout; it is being deleted, not
// extended. This wrapper is thin glue (applicability filters + summary + onSelect contract).
// See spec Reuse Audit.
import React, { useCallback } from 'react';
import { UnifiedFilters } from 'types/filters';
import type { SphereSearchResult } from '../../../hooks/useSphereSearch';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useAspectCatalog } from '../../../hooks/useAspectCatalog';
import SphereSelectDialogOrganism from './SphereSelectDialogOrganism';
import ApplicabilitySummaryMolecule from '../../molecules/marketplace/ApplicabilitySummaryMolecule';
import './DeployTargetDialogOrganism.scss';

export interface DeployTargetDialogOrganismProps {
  open: boolean;
  onClose: () => void;
  /**
   * Applicability from the approach definition.
   * `propertyFilters` are passed as fixed contextFilters to the search.
   * Null = unfiltered (permission:'edit' still applied).
   */
  applicability: UnifiedFilters | null;
  /**
   * ID of the sphere the user is currently on.
   * Highlights the matching row and enables the Nearby source tab.
   */
  currentSphereId?: string;
  /** Drop-in for the old DeployTargetPickerMolecule contract; closes on select. */
  onSelect: (sphereId: string, sphereName: string) => void;
}

const DeployTargetDialogOrganism: React.FC<DeployTargetDialogOrganismProps> = ({
  open,
  onClose,
  applicability,
  currentSphereId,
  onSelect,
}) => {
  const fixedContextFilters = applicability?.propertyFilters ?? [];
  const apiClients = useApiClientsOrNull();
  const { propertyOptions } = useAspectCatalog(apiClients);

  const handleSelect = useCallback(
    async (sphere: SphereSearchResult): Promise<boolean> => {
      const name = sphere.properties?.name || sphere.label;
      onSelect(sphere.id, name);
      return true;
    },
    [onSelect]
  );

  const headerExtras = applicability ? (
    <ApplicabilitySummaryMolecule
      applicability={applicability}
      properties={propertyOptions}
      className="deploy-target-dialog__applicability"
    />
  ) : null;

  return (
    <SphereSelectDialogOrganism
      open={open}
      onClose={onClose}
      titleKey="marketplace.deploy.targetDialog.title"
      onSelect={handleSelect}
      fixedContextFilters={fixedContextFilters.length > 0 ? fixedContextFilters : []}
      showContextSelector={false}
      permission="edit"
      highlightSphereId={currentSphereId}
      nearbyAnchorSphereId={currentSphereId}
      headerExtras={headerExtras}
    />
  );
};

export default DeployTargetDialogOrganism;
